/**
 * Firebase Utilities
 * Helper functions for Firebase operations
 */

import { Timestamp } from "firebase/firestore";
import { EXPIRATION } from "./constants";

/**
 * Get expiration timestamp for next 6th of month
 * @returns {Timestamp} Firebase Timestamp
 */
export function getNextSixthOfMonth() {
  const now = new Date();
  let year = now.getFullYear();
  let month = now.getMonth();
  
  // If we're past the 6th, move to next month
  if (now.getDate() >= EXPIRATION.DEFAULT_DAY_OF_MONTH) {
    month++;
    if (month > 11) {
      month = 0;
      year++;
    }
  }
  
  return Timestamp.fromDate(new Date(year, month, EXPIRATION.DEFAULT_DAY_OF_MONTH, 0, 0, 0));
}

/**
 * Get expiration timestamp X days from now
 * @param {number} days - Number of days
 * @returns {Timestamp} Firebase Timestamp
 */
export function getExpirationDaysFromNow(days = EXPIRATION.DEFAULT_DAYS_FROM_CREATION) {
  const now = new Date();
  const expiration = new Date(now.getTime() + days * 24 * 60 * 60 * 1000);
  return Timestamp.fromDate(expiration);
}

/**
 * Check if a request has expired
 * @param {Timestamp} expiresAt - Expiration timestamp
 * @returns {boolean} True if expired
 */
export function isExpired(expiresAt) {
  if (!expiresAt || !expiresAt.toDate) return false;
  return expiresAt.toDate() < new Date();
}

/**
 * Normalize contact ID (lowercase, no spaces)
 * @param {string} value - Contact value (email or phone)
 * @returns {string} Normalized ID
 */
export function normalizeContactId(value) {
  if (!value) return "";
  return value.replace(/\s+/g, "").toLowerCase();
}

/**
 * Build storage path for uploaded PDF
 * @param {string} id - Request ID
 * @param {string} folder - Folder name (default: "uploads")
 * @returns {string} Storage path
 */
export function buildStoragePath(id, folder = "uploads") {
  return `${folder}/${id}.pdf`;
}

/**
 * Build storage path for signed PDF
 * @param {string} id - Request ID
 * @returns {string} Storage path
 */
export function buildSignedPath(id) {
  return buildStoragePath(id, "signed");
}

/**
 * Build storage path for temp PDF (QR)
 * @param {string} id - Request ID
 * @returns {string} Storage path
 */
export function buildTempPath(id) {
  return buildStoragePath(id, "temp");
}

/**
 * Extract file extension from filename
 * @param {string} filename - Filename
 * @returns {string} Extension (without dot)
 */
export function getFileExtension(filename) {
  if (!filename) return "";
  const parts = filename.split(".");
  return parts.length > 1 ? parts.pop().toLowerCase() : "";
}

/**
 * Get base URL for the application
 * @returns {string} Base URL
 */
export function getBaseUrl() {
  return (import.meta.env.VITE_BASE_URL || window.location.origin).replace(/\/+$/, "");
}

/**
 * Build signature link for a request
 * @param {string} requestId - Request ID
 * @returns {string} Full signature URL
 */
export function buildSignatureLink(requestId) {
  return `${getBaseUrl()}/sign/${requestId}`;
}
